import { useState, useEffect } from "react";
import { RadioGroup } from "@headlessui/react";
import { useTranslation } from "react-i18next";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Structured allergy options - maps to backend proteinType values
const allergyOptions = [
  { id: 1, labelKey: "allergen_chicken", value: "chicken" },
  { id: 2, labelKey: "allergen_turkey", value: "turkey" },
  { id: 3, labelKey: "allergen_calf", value: "calf" },
  { id: 4, labelKey: "allergen_lamb", value: "lamb" },
  { id: 5, labelKey: "allergen_salmon", value: "salmon" },
  { id: 6, labelKey: "allergen_seabass", value: "seabass" },
];

const yesNoOptions = [{ value: true }, { value: false }];

function PetAlergic({ petInfo, setPetInfo, setIsComplete, petType }) {
  const { t } = useTranslation();

  // Parse existing allergies string into array
  const parseAllergies = (str) => {
    if (!str) return [];
    return str.split(',').map(a => a.trim()).filter(Boolean);
  };

  const [hasAlergic, setHasAlergic] = useState(
    petInfo.hasAlergic === true ? yesNoOptions[0] :
      petInfo.hasAlergic === false ? yesNoOptions[1] : null
  );
  const [selectedAllergies, setSelectedAllergies] = useState(parseAllergies(petInfo.allergies));
  const [otherAllergy, setOtherAllergy] = useState('');
  const [hasHealthIssues, setHasHealthIssues] = useState(
    petInfo.hasHealthIssues === true ? yesNoOptions[0] :
      petInfo.hasHealthIssues === false ? yesNoOptions[1] : null
  );
  const [disease, setDisease] = useState(petInfo.disease || '');

  // Initialize "other" text from existing allergies not in the checkbox list
  useEffect(() => {
    const knownValues = allergyOptions.map(a => a.value);
    const existingOther = selectedAllergies.filter(a => !knownValues.includes(a));
    if (existingOther.length > 0) {
      setOtherAllergy(existingOther.join(', '));
    }
  }, []);

  // Toggle allergy checkbox
  const toggleAllergy = (value) => {
    setSelectedAllergies(prev =>
      prev.includes(value)
        ? prev.filter(a => a !== value)
        : [...prev, value]
    );
  };

  // Sync allergies to petInfo as comma-separated string
  useEffect(() => {
    const knownValues = allergyOptions.map(a => a.value);
    const allAllergies = [
      ...selectedAllergies.filter(a => knownValues.includes(a)),
      ...(otherAllergy ? otherAllergy.split(',').map(a => a.trim()).filter(Boolean) : [])
    ];

    setPetInfo(prev => ({
      ...prev,
      hasAlergic: hasAlergic?.value ?? prev.hasAlergic,
      allergies: hasAlergic?.value ? allAllergies.join(', ') : '',
      hasHealthIssues: hasHealthIssues?.value ?? prev.hasHealthIssues,
      disease: hasHealthIssues?.value ? disease : '',
    }));
  }, [hasAlergic, selectedAllergies, otherAllergy, hasHealthIssues, disease]);

  // Form completion check (this step is optional, always passes)
  useEffect(() => {
    setIsComplete(true);
  }, [setIsComplete]);

  return (
    <div className="w-full max-w-lg flex flex-col px-4 sm:px-0">
      {/* Allergy Question */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="font-medium font-poppins text-lg text-yellow-600 text-left">
            {petType === 'dog' ? t("petHasAllergy") : t("catPetHasAllergy")}{' '}
            <span className="text-gray-400 text-sm font-normal">{t("optionalDefaultNo") || "(opsiyonel, varsayılan: Hayır)"}</span>
          </h2>
        </div>

        <RadioGroup value={hasAlergic} onChange={setHasAlergic} className="mt-2">
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            {yesNoOptions.map((option, index) => (
              <RadioGroup.Option
                key={index}
                value={option}
                className={({ checked }) =>
                  classNames(
                    "cursor-pointer focus:outline-none",
                    checked
                      ? "bg-kito-green border-transparent text-white hover:bg-opacity-90"
                      : "bg-white border-yellow-200 text-yellow-800 hover:bg-yellow-50",
                    "border rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                  )
                }
              >
                <RadioGroup.Label as="span">
                  {option.value ? (t("allergyYes") || "EVET") : (t("allergyNo") || "HAYIR")}
                </RadioGroup.Label>
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>

      {/* Allergy Checkbox List */}
      {hasAlergic?.value && (
        <div className="mb-6 animate-fade-in">
          <p className="text-sm text-yellow-600 font-medium mb-3">
            {t("selectAllergens") || "Alerjisi olan besinleri seçin:"}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {allergyOptions.map((allergy) => (
              <label
                key={allergy.id}
                className={classNames(
                  "flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all text-sm font-medium",
                  selectedAllergies.includes(allergy.value)
                    ? "border-kito-green bg-green-50 text-kito-green"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                )}
                onClick={() => toggleAllergy(allergy.value)}
              >
                <div className={classNames(
                  "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                  selectedAllergies.includes(allergy.value)
                    ? "bg-kito-green border-kito-green"
                    : "bg-white border-gray-300"
                )}>
                  {selectedAllergies.includes(allergy.value) && (
                    <svg className="w-3 h-3 text-white" viewBox="0 0 16 16" fill="none">
                      <path d="M13.707 4.293a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L6 8.586l5.293-5.293a1 1 0 0 1 1.414 0z" fill="currentColor" />
                    </svg>
                  )}
                </div>
                {t(allergy.labelKey)}
              </label>
            ))}
          </div>

          {/* Other / Free text */}
          <div className="relative z-0 w-full group">
            <input
              type="text"
              name="other_allergy"
              onChange={(e) => setOtherAllergy(e.target.value)}
              value={otherAllergy}
              className="block py-2.5 px-0 w-full text-lg text-yellow-600 bg-transparent border-0 border-b-2 border-yellow-400 appearance-none focus:outline-none focus:ring-0 focus:border-kito-green peer"
              placeholder=" "
            />
            <label
              htmlFor="other_allergy"
              className="peer-focus:font-medium font-poppins absolute left-0 text-sm text-yellow-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-kito-green peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
            >
              {t("otherAllergy") || "Diğer (virgülle ayırın)"}
            </label>
          </div>
        </div>
      )}

      {/* Health Issues Question */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="font-medium font-poppins text-lg text-yellow-600 text-left">
            {petType === 'dog' ? t("petHasDisease") : t("catPetHasDisease")}{' '}
            <span className="text-gray-400 text-sm font-normal">{t("optionalDefaultNo") || "(opsiyonel, varsayılan: Hayır)"}</span>
          </h2>
        </div>

        <RadioGroup value={hasHealthIssues} onChange={setHasHealthIssues} className="mt-2">
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
            {yesNoOptions.map((option, index) => (
              <RadioGroup.Option
                key={index}
                value={option}
                className={({ checked }) =>
                  classNames(
                    "cursor-pointer focus:outline-none",
                    checked
                      ? "bg-kito-green border-transparent text-white hover:bg-opacity-90"
                      : "bg-white border-yellow-200 text-yellow-800 hover:bg-yellow-50",
                    "border rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                  )
                }
              >
                <RadioGroup.Label as="span">
                  {option.value ? (t("diseaseYes") || "EVET") : (t("diseaseNo") || "HAYIR")}
                </RadioGroup.Label>
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>

      {/* Disease Input */}
      {hasHealthIssues?.value && (
        <div className="relative z-0 w-full mb-6 group animate-fade-in">
          <input
            type="text"
            name="disease_pet"
            onChange={(e) => setDisease(e.target.value)}
            value={disease}
            className="block py-2.5 px-0 w-full text-lg text-yellow-600 bg-transparent border-0 border-b-2 border-yellow-400 appearance-none focus:outline-none focus:ring-0 focus:border-kito-green peer"
            placeholder=" "
          />
          <label
            htmlFor="disease_pet"
            className="peer-focus:font-medium absolute left-0 text-sm text-yellow-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-kito-green peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
          >
            {t("enterDisease") || "Lütfen hastalığını yazınız."}
          </label>
        </div>
      )}
    </div>
  );
}

export default PetAlergic;
